// components/Recordings/RecordingsListPage.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Video, Play, Calendar, Film, Clock, CheckCircle2 } from "lucide-react";
import { PageHeader, EmptyState, AppBadge } from "../ui";
import apiClient from "../../utils/apiClient";
import Shimmer from "../Common/Shimmer";
import "./Recordings.scss";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
};

const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return null;
  const totalSec = Math.round(minutes * 60);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const RecordingsListPage = ({ batchId }) => {
  const [recordings, setRecordings] = useState([]);
  const [courseThumbnailUrl, setCourseThumbnailUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewStatus, setViewStatus] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRecordings = async () => {
      setLoading(true);
      try {
        const params = batchId ? `?batchId=${batchId}` : '';
        const { data } = await apiClient.get(`/videos/student/my-videos${params}`);
        const vids = data?.data || [];
        setRecordings(vids);
        setCourseThumbnailUrl(data?.courseThumbnailUrl || '');

        // Fetch view status for the batch (single API call)
        const resolvedBatchId = batchId || vids[0]?.batch?._id || vids[0]?.batch;
        if (resolvedBatchId) {
          try {
            const vsRes = await apiClient.get(`/videos/batch/${resolvedBatchId}/view-status`);
            setViewStatus(vsRes.data?.data || {});
          } catch {}
        }
      } catch {
        setRecordings([]);
      }
      setLoading(false);
    };
    fetchRecordings();
  }, [batchId]);

  if (loading) {
    return (
      <div className="recordings-page container-fluid px-3 px-md-4 py-4">
        <Shimmer height={48} count={1} />
        <div className="recordings-page__grid mt-4">
          {[...Array(6)].map((_, i) => (
            <Shimmer key={i} height={240} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="recordings-page ">
      {/* Header */}
      <div className="recordings-page__header">
        <PageHeader
          title="Class Recordings"
          description="Watch your class recordings and review past sessions"
          icon={Video}
          actions={
            recordings.length > 0 && (
              <span className="recordings-page__count">
                {recordings.length} video{recordings.length !== 1 ? "s" : ""}
              </span>
            )
          }
        />
      </div>

      {/* Empty State */}
      {recordings.length === 0 ? (
        <EmptyState
          icon={<Video size={40} />}
          title="No recordings yet"
          description="Class recordings will appear here once your instructor records a session."
        />
      ) : (
        /* Video Grid */
        <div className="recordings-page__grid">
          {recordings.map((rec) => (
            <div
              key={rec._id}
              className="rec-card"
              onClick={() => navigate(`/s/recordings/${rec._id}`)}
            >
              {/* Thumbnail */}
              <div className="rec-card__thumbnail">
                {(courseThumbnailUrl || rec.thumbnailUrl) ? (
                  <img
                    src={courseThumbnailUrl || rec.thumbnailUrl}
                    alt={rec.topic || rec.title || "Recording"}
                    loading="lazy"
                  />
                ) : (
                  <div className="rec-card__thumb-placeholder">
                    <Film />
                    <span>No Preview</span>
                  </div>
                )}

                {/* Play overlay */}
                <div className="rec-card__play-overlay">
                  <div className="rec-card__play-btn">
                    <Play />
                  </div>
                </div>

                {/* Viewed badge top-left */}
                {viewStatus[rec._id]?.viewed && (
                  <div className="rec-card__viewed-badge">
                    <CheckCircle2 size={12} />
                    Viewed
                  </div>
                )}

                {/* Duration + type overlay at bottom-right */}
                <div className="rec-card__overlay-tags">
                  {rec.fileType && (
                    <span className="rec-card__file-type">{rec.fileType}</span>
                  )}
                  {formatDuration(rec.duration) && (
                    <span className="rec-card__duration">
                      <Clock size={10} />
                      {formatDuration(rec.duration)}
                    </span>
                  )}
                </div>
              </div>

              {/* Body */}
              <div className="rec-card__body">
                <h4 className="rec-card__title">
                  {rec.topic || rec.title || "Recording"}
                </h4>
                <div className="rec-card__meta">
                  <span>
                    <Calendar size={12} />
                    {formatDate(rec.recordedAt)}
                  </span>
                  <span>
                    <AppBadge variant="secondary" size="sm">
                      {rec.fileType || "MP4"}
                    </AppBadge>
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecordingsListPage;
